import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Typography
} from '@mui/material';
import { ShoppingCart as CartIcon } from '@mui/icons-material';
import { ReorderSuggestion } from '../types';

interface ReorderSuggestionsProps {
  suggestions: ReorderSuggestion[];
  onCreatePurchaseOrder?: () => void;
}

export const ReorderSuggestions: React.FC<ReorderSuggestionsProps> = ({ suggestions, onCreatePurchaseOrder }) => {
  const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case 'URGENT':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  if (suggestions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="textSecondary">
          No reorder suggestions at this time
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          All inventory levels are adequate
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Reorder Suggestions ({suggestions.length})</Typography>
        <Button 
          variant="contained" 
          startIcon={<CartIcon />}
          color="primary"
          onClick={onCreatePurchaseOrder}
          disabled={!onCreatePurchaseOrder}
        >
          Create Bulk PO
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Current Stock</TableCell>
              <TableCell align="right">Reorder Point</TableCell>
              <TableCell align="right">Suggested Qty</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell align="right">Est. Cost</TableCell>
              <TableCell>Lead Time</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suggestions.map((suggestion, index) => (
              <TableRow key={`${suggestion.SKU}-${index}`} hover>
                <TableCell>{suggestion.SKU}</TableCell>
                <TableCell>{suggestion.Product}</TableCell>
                <TableCell align="right">{suggestion.CurrentStock}</TableCell>
                <TableCell align="right">{suggestion.ReorderPoint}</TableCell>
                <TableCell align="right">
                  <strong>{suggestion.SuggestedQuantity}</strong>
                </TableCell>
                <TableCell>{suggestion.SupplierName}</TableCell>
                <TableCell align="right">{formatCurrency(suggestion.EstimatedCost)}</TableCell>
                <TableCell>{suggestion.LeadTime} days</TableCell>
                <TableCell>
                  <Chip 
                    label={suggestion.Priority} 
                    size="small" 
                    color={getPriorityColor(suggestion.Priority)}
                  />
                </TableCell>
                <TableCell>
                  <Button size="small" variant="outlined">
                    Create PO
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
